import React from 'react'
import { useDispatch } from 'react-redux'
import { useModalActions } from '@babylon/medkit'
import { useConfig } from '@/redux/partnerConfig/selectors'
import { useWebAppUrl } from '@/redux/productConfig/selectors'
import { useAuth0Config } from '@/redux/auth0Config/selectors'
import { WEB_APP_URL } from '@/config/constants'
import { useUserLocale } from '@/redux/userConfig/selectors'
import { setSignupProgress } from '@/redux/userConfig/actions'
import { OnlyNewUserModal } from '@/components'
import { useSignupRoutes, useActiveStepIndex } from '@/redux/signup/selectors'
import { useTrackingScreenTitle, useAuth0Wrapper } from '@/hooks'
import { trackTealiumEvent, trackTealiumView } from '@/tracking'

const useHandleExistingUser = () => {
  const dispatch = useDispatch()
  const { restrictToNewUsers = false, existingUserRedirectPath } = useConfig()
  const stepTrackingName = useTrackingScreenTitle()
  const { openModal } = useModalActions()
  const { branding } = useAuth0Config()
  const userLocale = useUserLocale()
  const routes = useSignupRoutes()
  const activeStepIndex = useActiveStepIndex()
  const { loginWithRedirect } = useAuth0Wrapper()
  const webAppUrl = useWebAppUrl()
  const loginRedirectUrl = `${window.location.origin}${routes[1].path}`
  const loginScreenTitle = 'login/'

  const trackOnlyNewUserModal = () => {
    trackTealiumEvent({
      screen_title: stepTrackingName,
      eventCategory: 'signup',
      eventLabel: 'this offer is only available to new patients',
      eventAction: 'registration error',
      tealium_event: 'registration error',
    })
  }

  const trackLoginModal = () => {
    trackTealiumView({
      screen_title: loginScreenTitle,
      tealium_event: loginScreenTitle,
    })

    trackTealiumEvent({
      screen_title: loginScreenTitle,
      eventCategory: 'login',
      eventAction: 'started login',
      tealium_event: 'started login',
    })
  }

  const openOnlyNewUserModal = () => openModal(<OnlyNewUserModal />)

  const handleExistingUser = (email: string) => {
    if (restrictToNewUsers) {
      if (existingUserRedirectPath) {
        if (existingUserRedirectPath.startsWith(WEB_APP_URL)) {
          const redirectUrl = existingUserRedirectPath.replace(
            WEB_APP_URL,
            webAppUrl
          )
          window.location.href = redirectUrl
        } else {
          window.location.replace(existingUserRedirectPath)
        }
      } else {
        trackOnlyNewUserModal()
        openOnlyNewUserModal()
      }
    } else {
      const loginProps = {
        scope: 'openid',
        mode: 'already-exists',
        appState: {
          redirectUrl: loginRedirectUrl,
        },
        login_hint: email,
        ...(!!branding && { branding }),
        ...(!!userLocale && { ui_locales: userLocale }),
      }

      trackLoginModal()
      // It's needed to increase the signup progress to allow
      // the user to be moved to the next step without being redirected
      dispatch(setSignupProgress(activeStepIndex + 1))
      loginWithRedirect(loginProps)
    }
  }

  return handleExistingUser
}

export default useHandleExistingUser
